// ─────────────────────────────────────────────────────────────────────────────
// utils/dateUtils.js
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Converts a Firestore Timestamp, ISO string, JS Date, or null/undefined
 * into a JS Date object — or null if the value is absent / invalid.
 *
 * Handles all formats that may arrive from Firestore or form inputs:
 *  • Firestore Timestamp  → { seconds, nanoseconds }
 *  • Firestore-like object → { _seconds, _nanoseconds }
 *  • JS Date              → returned as-is
 *  • ISO string / number  → passed through new Date()
 *  • null / undefined     → returns null
 *
 * @param  {*} value - Raw timestamp value from DB or form
 * @returns {Date|null}
 */
export const safeTimestampToDate = (value) => {
  if (!value) return null;

  // Already a JS Date
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  // Firestore Timestamp object (toDate method available)
  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }

  // Plain object with seconds (Firestore serialized shape)
  if (typeof value === 'object') {
    const seconds =
      value.seconds      ??   // Firestore Timestamp
      value._seconds     ??   // Serialized Firestore Timestamp
      null;

    if (seconds !== null) {
      return new Date(seconds * 1000);
    }

    return null;
  }

  // String or number — try native Date parse
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// FORMATTING HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Formats a date value to a locale date string.
 * Returns a fallback string when the value is null / invalid.
 *
 * @param  {*}      value    - Any value accepted by safeTimestampToDate
 * @param  {string} fallback - Returned when date is null (default "—")
 * @param  {object} options  - Intl.DateTimeFormat options
 * @returns {string}
 */
export const formatDate = (value, fallback = '—', options = {}) => {
  const date = safeTimestampToDate(value);
  if (!date) return fallback;

  return date.toLocaleDateString(undefined, {
    day:   '2-digit',
    month: 'short',
    year:  'numeric',
    ...options,
  });
};

/**
 * Formats a date value to a locale date-time string.
 *
 * @param  {*}      value    - Any value accepted by safeTimestampToDate
 * @param  {string} fallback - Returned when date is null (default "—")
 * @returns {string}
 */
export const formatDateTime = (value, fallback = '—') => {
  const date = safeTimestampToDate(value);
  if (!date) return fallback;

  return date.toLocaleString(undefined, {
    day:    '2-digit',
    month:  'short',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
  });
};

/**
 * Converts a JS Date or Firestore Timestamp to an ISO date string (YYYY-MM-DD).
 * Used to pre-populate <input type="date"> fields.
 *
 * @param  {*}      value    - Any value accepted by safeTimestampToDate
 * @param  {string} fallback - Returned when date is null (default "")
 * @returns {string}
 */
export const toInputDateString = (value, fallback = '') => {
  const date = safeTimestampToDate(value);
  if (!date) return fallback;

  // toISOString() returns UTC — shift to local date to avoid off-by-one
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPARISON HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns true if the given date is strictly in the past (before today's midnight).
 *
 * @param  {*} value - Any value accepted by safeTimestampToDate
 * @returns {boolean}
 */
export const isExpired = (value) => {
  const date = safeTimestampToDate(value);
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

/**
 * Returns true if the given date falls within the next `days` calendar days.
 *
 * @param  {*}      value - Any value accepted by safeTimestampToDate
 * @param  {number} days  - Look-ahead window in days (default 30)
 * @returns {boolean}
 */
export const isExpiringSoon = (value, days = 30) => {
  const date = safeTimestampToDate(value);
  if (!date) return false;

  const today   = new Date();
  today.setHours(0, 0, 0, 0);

  const horizon = new Date(today);
  horizon.setDate(horizon.getDate() + days);

  return date >= today && date <= horizon;
};

/**
 * Returns the number of days between today (midnight) and the given date.
 * Negative values indicate the date is in the past.
 *
 * @param  {*} value - Any value accepted by safeTimestampToDate
 * @returns {number|null} null when the value is invalid
 */
export const daysFromToday = (value) => {
  const date = safeTimestampToDate(value);
  if (!date) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  return Math.round((date.getTime() - today.getTime()) / MS_PER_DAY);
};